import { TrendingDown, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import type { ProjectoPLSummary } from '../../types/projecto';

interface Props {
  summary: ProjectoPLSummary;
}

function fmt(v: number) {
  return v.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
}
function fmtPct(v: number) {
  return `${v.toFixed(1)}%`;
}

function MetricBlock({
  label,
  value,
  sub,
  color = 'default',
  large = false,
}: {
  label: string;
  value: string;
  sub?: string;
  color?: 'default' | 'green' | 'red' | 'amber' | 'blue';
  large?: boolean;
}) {
  const valueColor = {
    default: 'text-gray-900',
    green: 'text-emerald-700',
    red: 'text-red-600',
    amber: 'text-amber-600',
    blue: 'text-blue-700',
  }[color];

  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest whitespace-nowrap">{label}</span>
      <span className={`${large ? 'text-base' : 'text-sm'} font-bold ${valueColor} leading-tight tabular-nums whitespace-nowrap`}>{value}</span>
      {sub && <span className="text-[11px] text-gray-400 tabular-nums whitespace-nowrap">{sub}</span>}
    </div>
  );
}

export function ProjectoCalculatorFooter({ summary }: Props) {
  const {
    billableToClient,
    absorbedCosts,
    totalCosts,
    netProfit,
    netMarginPct,
    agencyMargin,
    agencyMarginPct,
    outstandingReceivables,
    outstandingPayables,
    netCashPosition,
    paidCosts,
    pendingCosts,
  } = summary;

  const marginColor: 'green' | 'amber' | 'red' =
    agencyMarginPct >= 20 ? 'green' : agencyMarginPct >= 5 ? 'amber' : 'red';
  const netColor: 'green' | 'amber' | 'red' =
    netMarginPct >= 20 ? 'green' : netMarginPct >= 5 ? 'amber' : 'red';
  const cashColor: 'blue' | 'red' = netCashPosition >= 0 ? 'blue' : 'red';

  const costPaidPct = totalCosts > 0 ? (paidCosts / totalCosts) * 100 : 0;

  return (
    <div className="flex-shrink-0 bg-white border-t-2 border-gray-100 shadow-[0_-4px_20px_-4px_rgba(0,0,0,0.08)]">
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4 px-5 py-3">
        <MetricBlock label="Faturado ao Cliente" value={fmt(billableToClient)} large />
        <MetricBlock label="Custos Absorvidos" value={fmt(absorbedCosts)} color="red" />
        <MetricBlock
          label="Lucro Líquido"
          value={fmt(netProfit)}
          sub={fmtPct(netMarginPct) + ' margem'}
          color={netColor}
          large
        />
        <MetricBlock
          label="Margem Agência"
          value={fmt(agencyMargin)}
          sub={fmtPct(agencyMarginPct)}
          color={marginColor}
          large
        />

        <div className="flex flex-col gap-0.5">
          <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest whitespace-nowrap">Custos Pagos</span>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-gray-700 tabular-nums whitespace-nowrap">{fmt(paidCosts)}</span>
            <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden flex-shrink-0">
              <div
                className="h-full bg-emerald-500 rounded-full transition-all"
                style={{ width: `${Math.min(100, costPaidPct)}%` }}
              />
            </div>
          </div>
          <span className="text-[11px] text-gray-400 tabular-nums whitespace-nowrap">{fmt(pendingCosts)} pendente</span>
        </div>

        <div className="flex flex-col gap-0.5">
          <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest flex items-center gap-1 whitespace-nowrap">
            <ArrowUpRight className="w-3 h-3 text-emerald-500" />A Receber
          </span>
          <span className="text-sm font-bold text-emerald-700 tabular-nums whitespace-nowrap">{fmt(outstandingReceivables)}</span>
        </div>

        <div className="flex flex-col gap-0.5">
          <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest flex items-center gap-1 whitespace-nowrap">
            <ArrowDownRight className="w-3 h-3 text-red-500" />A Pagar
          </span>
          <span className="text-sm font-bold text-red-600 tabular-nums whitespace-nowrap">{fmt(outstandingPayables)}</span>
        </div>

        <MetricBlock
          label="Posição Caixa"
          value={fmt(netCashPosition)}
          color={cashColor}
          large
        />
      </div>

      {netProfit < 0 && (
        <div className="flex items-center gap-1.5 mx-5 mb-3 px-3 py-1.5 bg-red-50 rounded-lg border border-red-200 w-fit">
          <TrendingDown className="w-3.5 h-3.5 text-red-600" />
          <span className="text-xs font-semibold text-red-700">Margem negativa</span>
        </div>
      )}
    </div>
  );
}
